import { useCallback, useSyncExternalStore } from 'react';

export type RemixCancellationScope = 'outer-lifecycle' | 'adjustment-request';

export type RemixCancellationIntent = {
	readonly sessionId: string;
	readonly conversationId: string;
	readonly scope: RemixCancellationScope;
	readonly adjustmentId?: string;
};

export type RemixCancellationSignal = RemixCancellationIntent;

export type RemixCancellationSnapshot = {
	readonly sessionId?: string;
	readonly conversationId?: string;
	readonly isObservationReady?: boolean;
	readonly pendingSignals: readonly RemixCancellationSignal[];
};

type ActiveSession = {
	readonly sessionId: string;
	readonly conversationId: string;
	readonly outerCancellationIntent: RemixCancellationIntent;
	readonly isObservationReady: boolean;
	readonly activeAdjustmentId?: string;
	readonly pendingSignals: readonly RemixCancellationSignal[];
};

type RemixCancellationSessionAddress = {
	sessionId: string;
	conversationId: string;
};

const EMPTY_SNAPSHOT: RemixCancellationSnapshot = Object.freeze({
	pendingSignals: Object.freeze([]),
});

let nextIdentity = 0;
let activeSession: ActiveSession | undefined;
// One-entry memo so repeated reads of the active adjustment stay reference-stable for callers that
// bind the intent during render. Keyed by session and adjustment, so it can never be inherited by a
// replacement session on the same conversation.
let capturedAdjustmentIntent: RemixCancellationIntent | undefined;
const subscribers = new Set<() => void>();

// Single construction site for the adjustment intent, so the surface that starts an adjustment and
// the surface that later captures it can never disagree about its shape.
const createAdjustmentIntent = (
	sessionId: string,
	conversationId: string,
	adjustmentId: string,
): RemixCancellationIntent =>
	Object.freeze({ sessionId, conversationId, scope: 'adjustment-request', adjustmentId });

const nextId = (prefix: 'session' | 'adjustment'): string => {
	nextIdentity += 1;
	return `remix-cancellation-${prefix}-${nextIdentity}`;
};

const notifySubscribers = (): void => {
	subscribers.forEach((subscriber) => {
		try {
			subscriber();
		} catch {
			// Shadow observation must never affect the authoritative legacy flow.
		}
	});
};

export const registerRemixCancellationSession = ({
	conversationId,
}: {
	conversationId: string;
}): string | undefined => {
	if (activeSession) {
		return undefined;
	}

	const sessionId = nextId('session');
	const outerCancellationIntent: RemixCancellationIntent = Object.freeze({
		sessionId,
		conversationId,
		scope: 'outer-lifecycle',
	});
	activeSession = {
		sessionId,
		conversationId,
		outerCancellationIntent,
		isObservationReady: false,
		pendingSignals: EMPTY_SNAPSHOT.pendingSignals,
	};
	notifySubscribers();
	return sessionId;
};

export const unregisterRemixCancellationSession = (sessionId: string): void => {
	if (activeSession?.sessionId !== sessionId) {
		return;
	}
	activeSession = undefined;
	notifySubscribers();
};

export const activateRemixCancellationObservation = ({
	conversationId,
	sessionId,
}: RemixCancellationSessionAddress): boolean => {
	if (
		!activeSession ||
		activeSession.conversationId !== conversationId ||
		activeSession.sessionId !== sessionId
	) {
		return false;
	}

	activeSession = { ...activeSession, isObservationReady: true };
	notifySubscribers();
	return true;
};

export const captureRemixCancellationIntent = ({
	conversationId,
}: {
	conversationId: string;
	scope: 'outer-lifecycle';
}): RemixCancellationIntent | undefined => {
	if (activeSession?.conversationId !== conversationId) {
		return undefined;
	}

	return activeSession.outerCancellationIntent;
};

export const startRemixAdjustment = (
	conversationId: string,
): RemixCancellationIntent | undefined => {
	if (activeSession?.conversationId !== conversationId) {
		return undefined;
	}

	const adjustmentId = nextId('adjustment');
	activeSession = { ...activeSession, activeAdjustmentId: adjustmentId };
	// Seed the memo here so the surface that starts the adjustment and any surface that later
	// captures it hold the very same intent, not two equal copies.
	capturedAdjustmentIntent = createAdjustmentIntent(
		activeSession.sessionId,
		conversationId,
		adjustmentId,
	);
	notifySubscribers();
	return capturedAdjustmentIntent;
};

/**
 * Lets a surface that did not start the adjustment (Rovo Chat's Stop) cancel the same operation.
 * Returns the identity created by `startRemixAdjustment` so no new operation identity is minted,
 * and `undefined` once the adjustment has settled — the caller then falls back to the outer intent.
 */
export const captureActiveRemixAdjustmentIntent = ({
	conversationId,
}: {
	conversationId: string;
}): RemixCancellationIntent | undefined => {
	const session = activeSession;
	if (session?.conversationId !== conversationId || !session.activeAdjustmentId) {
		return undefined;
	}

	if (
		capturedAdjustmentIntent?.sessionId !== session.sessionId ||
		capturedAdjustmentIntent.adjustmentId !== session.activeAdjustmentId
	) {
		capturedAdjustmentIntent = createAdjustmentIntent(
			session.sessionId,
			conversationId,
			session.activeAdjustmentId,
		);
	}

	return capturedAdjustmentIntent;
};

/**
 * Releases ownership of a settled adjustment so a later, unrelated Stop cannot inherit it and be
 * misread as an adjustment stop. Ignores identities that no longer own the active adjustment.
 */
export const finishRemixAdjustment = ({
	conversationId,
	adjustmentId,
}: {
	conversationId: string;
	adjustmentId: string;
}): void => {
	const session = activeSession;
	if (
		!session ||
		session.conversationId !== conversationId ||
		session.activeAdjustmentId !== adjustmentId
	) {
		return;
	}

	activeSession = { ...session, activeAdjustmentId: undefined };
	notifySubscribers();
};

export const recordRemixCancellation = ({
	sessionId,
	conversationId,
	scope,
	adjustmentId,
}: RemixCancellationIntent): RemixCancellationSignal | undefined => {
	const session = activeSession;
	if (!session || session.sessionId !== sessionId || session.conversationId !== conversationId) {
		return undefined;
	}

	// A stale adjustment identity never cancels the adjustment that is currently active.
	if (
		scope === 'adjustment-request' &&
		(!adjustmentId || adjustmentId !== session.activeAdjustmentId)
	) {
		return undefined;
	}

	const duplicate = session.pendingSignals.find(
		(signal) => signal.scope === scope && signal.adjustmentId === adjustmentId,
	);
	if (duplicate) {
		return duplicate;
	}

	const signal: RemixCancellationSignal = Object.freeze({
		sessionId,
		conversationId,
		scope,
		...(adjustmentId ? { adjustmentId } : {}),
	});
	activeSession = { ...session, pendingSignals: [...session.pendingSignals, signal] };
	notifySubscribers();
	return signal;
};

export const claimNextRemixCancellation = ({
	sessionId,
	conversationId,
}: RemixCancellationSessionAddress): RemixCancellationSignal | undefined => {
	const session = activeSession;
	if (!session || session.sessionId !== sessionId || session.conversationId !== conversationId) {
		return undefined;
	}

	const [signal, ...pendingSignals] = session.pendingSignals;
	if (!signal) {
		return undefined;
	}

	activeSession = { ...session, pendingSignals };
	notifySubscribers();
	return signal;
};

export const getRemixCancellationSnapshot = ({
	sessionId,
	conversationId,
}: RemixCancellationSessionAddress): RemixCancellationSnapshot => {
	const session = activeSession;
	if (!session || session.sessionId !== sessionId || session.conversationId !== conversationId) {
		return EMPTY_SNAPSHOT;
	}
	return session;
};

export const subscribeToRemixCancellationSignals = (subscriber: () => void): (() => void) => {
	subscribers.add(subscriber);
	return () => subscribers.delete(subscriber);
};

export const useRemixCancellationSnapshot = ({
	sessionId,
	conversationId,
}: RemixCancellationSessionAddress): RemixCancellationSnapshot => {
	const getSnapshot = useCallback(
		() => getRemixCancellationSnapshot({ sessionId, conversationId }),
		[conversationId, sessionId],
	);
	return useSyncExternalStore(subscribeToRemixCancellationSignals, getSnapshot, getSnapshot);
};

export const clearRemixCancellationSignalsForTesting = (): void => {
	activeSession = undefined;
	// Identities restart from zero, so a retained intent could match a fresh key by value.
	capturedAdjustmentIntent = undefined;
	nextIdentity = 0;
	subscribers.clear();
};
